import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Sun, Moon, ChevronDown, Globe } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { LANGUAGES } from "@/i18n";
import LanguageSwitcher from "./LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { InquiryDialog } from "@/components/InquiryDialog";

const NAV_LINKS = [
  { to: "/about", key: "about" },
  { to: "/products", key: "products" },
  { to: "/roasted-green-tea", key: "roasted_green_tea", highlight: true },
  { to: "/industries", key: "industries" },
  { to: "/oem", key: "oem" },
  { to: "/sustainability", key: "sustainability" },
  { to: "/quality", key: "quality" },
  { to: "/export-process", key: "export_process" },
  { to: "/blog", key: "blog" },
  { to: "/resources", key: "resources" },
  { to: "/faq", key: "faq" },
  { to: "/contact", key: "contact" },
];

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isRTL = i18n.dir() === "rtl";

  return (
    <>
      <header
        data-testid="main-navigation"
        className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-background/85 border-b border-border"
            : "backdrop-blur-md bg-background/40"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="flex items-center justify-between h-20">
            <Link
              to="/"
              data-testid="nav-logo"
              className="flex items-center gap-3 group shrink-0"
            >
              <img
                src="/logo.png"
                alt="PT Agrikultural Nusantara Perkasa"
                data-testid="nav-logo-image"
                className={`h-11 md:h-12 w-auto object-contain transition-all duration-500 group-hover:scale-[1.03] ${
                  scrolled
                    ? "drop-shadow-none"
                    : "drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)]"
                }`}
              />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden xl:flex items-center gap-6 text-sm">
              {NAV_LINKS.slice(0, 8).map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.key}`}
                  className={({ isActive }) =>
                    `nav-link transition-colors ${
                      isActive
                        ? "active text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    } ${l.highlight ? "text-gold-500 dark:text-gold-300" : ""}`
                  }
                >
                  {t(`nav.${l.key}`)}
                </NavLink>
              ))}
              <div className="relative group">
                <button
                  data-testid="nav-more"
                  className="nav-link text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  {t("common.of").length ? "•••" : "More"}
                  <ChevronDown className="w-3 h-3" />
                </button>
                <div className="absolute end-0 top-full mt-3 min-w-[220px] bg-card border border-border rounded shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  {NAV_LINKS.slice(8).map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      data-testid={`nav-link-${l.key}`}
                      className={({ isActive }) =>
                        `block px-5 py-3 text-sm transition-colors ${
                          isActive ? "text-foreground bg-muted" : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                        }`
                      }
                    >
                      {t(`nav.${l.key}`)}
                    </NavLink>
                  ))}
                </div>
              </div>
            </nav>

            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <button
                onClick={toggle}
                aria-label={t("nav.theme")}
                data-testid="theme-toggle"
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <div className="hidden md:block">
                <InquiryDialog
                  type="quote"
                  trigger={
                    <Button
                      data-testid="nav-cta-quote"
                      className="rounded-full bg-forest-600 hover:bg-forest-700 text-white h-10 px-5 font-normal tracking-wide"
                    >
                      {t("cta.get_quotation")}
                    </Button>
                  }
                />
              </div>
              <button
                onClick={() => setMobileOpen(true)}
                data-testid="nav-mobile-open"
                className="xl:hidden p-2 rounded-full hover:bg-muted transition-colors"
                aria-label={t("nav.menu")}
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-background/98 backdrop-blur-xl animate-in fade-in duration-300">
          <div className="flex items-center justify-between h-20 px-6 md:px-10">
            <img
              src="/logo.png"
              alt="PT Agrikultural Nusantara Perkasa"
              className="h-10 w-auto object-contain"
            />
            <button
              onClick={() => setMobileOpen(false)}
              data-testid="nav-mobile-close"
              className="p-2 rounded-full hover:bg-muted"
              aria-label={t("nav.close")}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="px-6 md:px-10 pt-6 pb-12 overflow-y-auto max-h-[calc(100vh-5rem)]">
            <div className="flex flex-col gap-1">
              <NavLink
                to="/"
                data-testid="mobile-nav-link-home"
                className={({ isActive }) =>
                  `py-4 border-b border-border font-serif text-2xl md:text-3xl ${
                    isActive ? "text-forest-600 dark:text-forest-400" : "text-foreground"
                  }`
                }
              >
                {t("nav.home")}
              </NavLink>
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.key}`}
                  className={({ isActive }) =>
                    `py-4 border-b border-border font-serif text-2xl md:text-3xl ${
                      isActive ? "text-forest-600 dark:text-forest-400" : "text-foreground"
                    } ${l.highlight ? "text-gold-500 dark:text-gold-300" : ""}`
                  }
                >
                  {t(`nav.${l.key}`)}
                </NavLink>
              ))}
            </div>
            <div className="mt-10 flex flex-col gap-4">
              <InquiryDialog
                type="quote"
                trigger={
                  <Button
                    data-testid="mobile-cta-quote"
                    className="rounded-full bg-forest-600 hover:bg-forest-700 text-white h-12 w-full"
                  >
                    {t("cta.get_quotation")}
                  </Button>
                }
              />
              <InquiryDialog
                type="sample"
                trigger={
                  <Button
                    data-testid="mobile-cta-sample"
                    variant="outline"
                    className="rounded-full h-12 w-full border-forest-600 text-forest-600 dark:text-forest-300 dark:border-forest-500"
                  >
                    {t("cta.request_sample")}
                  </Button>
                }
              />
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Navbar;
